// stores/useAuthStore.ts
import { create } from "zustand";
import Cookies from "js-cookie";
import { useRouter } from "next/router";

const tokenName = "ch_accessToken";
const refreshTokenName = "ch_refreshToken";

interface AuthState {
  user: {
    id?: string;
    email?: string;
    fullName?: string;
    systemRole?: string;
    onlyMe?: boolean;
    onlyAssigned?: boolean;
    onlyView?: boolean;
    organizations?: any[];
  } | null;
  authenticating: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  isSuperAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  accessToken?: string;
  refreshToken?: string;
  fetchUser: (router?: ReturnType<typeof useRouter>) => Promise<void>;
  authenticateUser: (email: string, password: string) => Promise<void>;
  authenticateToken: (token: string | undefined) => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set, get) => {
  const saveAccessToken = (token: string) =>
    Cookies.set(tokenName, token, { expires: 1 });
  const saveRefreshToken = (token: string) =>
    Cookies.set(refreshTokenName, token, { expires: 1 });
  const getAccessToken = () => Cookies.get(tokenName);
  const getRefreshToken = () => Cookies.get(refreshTokenName);
  const removeAccessToken = () => Cookies.remove(tokenName);
  const removeRefreshToken = () => Cookies.remove(refreshTokenName);

  return {
    user: null,
    authenticating: true,
    error: null,
    isAuthenticated: false,
    isAuthorized: false,
    isSuperAdmin: false,

    accessToken: getAccessToken(),
    refreshToken: getRefreshToken(),

    login: async (email: string, password: string) => {
      await get().authenticateUser(email, password);
    },

    logout: async () => {
      removeAccessToken();
      removeRefreshToken();
      set({
        isAuthenticated: false,
        isAuthorized: false,
        isSuperAdmin: false,
        user: null,
      });
      // Redirect to the login page
      location.href = "/";
    },

    authenticateUser: async (email: string, password: string) => {
      const response = await fetch("/api/ats/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const { token } = await response.json();
      const { accessToken, refreshToken } = token;
      saveAccessToken(accessToken);
      saveRefreshToken(refreshToken);
      set({
        accessToken,
        refreshToken,
      });

      // Fetch user data immediately after successful login
      try {
        const userResponse = await fetch("/api/ats/auth/me", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (userResponse.ok) {
          const user = await userResponse.json();
          set({
            user,
            isAuthenticated: true,
            isAuthorized: true,
            authenticating: false,
          });
        } else {
          throw new Error("Failed to fetch user data");
        }
      } catch (error) {
        console.error("Error fetching user after login:", error);
        // Still set as authenticated but without user data
        set({
          isAuthenticated: true,
          isAuthorized: true,
          authenticating: false,
        });
      }
    },

    authenticateToken: async (userToken) => {
      try {
        if (!userToken) throw new Error("Invalid token");
        set({
          isAuthenticated: true,
          isAuthorized: true,
          authenticating: false,
        });
        return true;
      } catch (error) {
        return false;
      }
    },

    fetchUser: async (router) => {
      try {
        console.log("Fetching user...");
        const accessToken = getAccessToken();
        console.log("Access Token:", accessToken);

        if (!accessToken) {
          set({ authenticating: false });
          if (router?.isReady) {
            router.push("/");
          }
          return;
        }

        const response = await fetch("/api/ats/auth/me", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const user = await response.json();
        set({
          user,
          isAuthenticated: true,
          isAuthorized: true,
          authenticating: false,
        });
      } catch (err: any) {
        console.error("Error fetching user:", err);
        set({ authenticating: false });
        if (router?.isReady) {
          router.push("/");
        }
      }
    },
  };
});
